import { useState, useEffect } from "react"
import {
  getProjectSyncOrganizationsMiddleware,
  getOrganizationProjectDataMiddleware,
  syncCommitToOrganizationMiddleware,
  handleUserShareKeyProjectMiddleware,
  addRoleGroupOrganizationMiddleware,
} from "./services/api"
import { toast } from "react-toastify"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"
import { getUserMiddleware } from "pages/auth/services/api"
import { cloneDeep } from "lodash"
import { ReactComponent as IconCheckWarning } from "assets/images/icons/icon-warning-notification.svg"
import { ReactComponent as IconRemove } from "assets/images/icons/icon-clear-search.svg"
import { ReactComponent as IconArrowRight } from "assets/images/icons/icon-arrow-right.svg"
import TypeComponent from "pages/project-component/molecules/TypeComponent"
import CheckedDefault from "components/Checked/CheckedDefault"
import { EncryptionKeys } from "controllers/EncryptionController"
import { encryptionGCMHelper } from "helpers/encryption-gcm"
import SelectMulti from "components/Select/SelectMulti"
import Button from "components/Button/Button"
import PageLayout from "pages/layout/PageLayout"
import { useParams } from "react-router-dom"
import { useAppSelector } from "hooks/useApp"
import { configureStore } from "stores/configureStore"
import { closeModal, openModal } from "reducers/modal"
import { pushTo } from "helpers/history"
import { PATH } from "constants/path"
import HashLoader from "react-spinners/HashLoader"
import Tippy from "@tippyjs/react"
import { Pagination } from "components/Pagination/Pagination"
import { useNumber } from "helpers/hooks"
import { closeLoading, startLoading } from "reducers/loading"

export const SyncToOrganizationPage = () => {
  const userInfo = useAppSelector((state) => state.userInfo)
  const params = useParams<{
    projectId: string
  }>()
  const projectId = params.projectId || ""
  const [sourceOrganizations, setSourceOrganizations] = useState<
    {
      label: string
      value: string
      db_name: string
    }[]
  >([])
  const [destinationOrganizations, setDestinationOrganizations] = useState<
    {
      label: string
      value: string
      db_name: string
    }[]
  >([])

  const [selectedSourceOrganization, setSelectedSourceOrganization] = useState<{
    label: string
    value: string
    db_name: string
  }>({
    label: "",
    value: "",
    db_name: "",
  })

  const [selectedDestinationOrganization, setSelectedDestinationOrganization] =
    useState<{
      label: string
      value: string
      db_name: string
    }>({
      label: "",
      value: "",
      db_name: "",
    })

  const [projectSyncOrganization, setProjectSyncOrganization] = useState<{
    source: {
      project: {
        code: string
        id: string
        fullCode: string
      }
    }
    destination: {
      hasProject: boolean
      project: {
        code: string
        id: string
        fullCode: string
      }
      components: {
        source: {
          code: string
          id: string
          type: string
          historyCode: string
          historyId: string
          historyCommit: string
        }
        destination: {
          code: string
          id: string
          type: string
          historyCode: string
          historyId: string
        } | null
        isNewComponent: boolean
      }[]
    }
  }>({
    source: {
      project: {
        code: "",
        id: "",
        fullCode: "",
      },
    },
    destination: {
      hasProject: false,
      project: {
        code: "",
        id: "",
        fullCode: "",
      },
      components: [],
    },
  })

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [errorDestination, setErrorDestination] = useState<string>("")

  const [selectSyncComponent, setSelectSyncComponent] = useState<
    Record<
      string,
      {
        historyId: string
        componentId: string
        historyCommit: string
        sourceCode: string
        destinationCode: string
        newComponentCode: string
        oldComponentCode: string
        newProjectCode: string
        oldProjectCode: string
      }
    >
  >({})

  // Pagination states
  const [paginationState, setPaginationState] = useState({
    page: 1,
    pageSize: 25,
  })
  const totalPage = useNumber(0)

  // Gọi API khi component mount
  useEffect(() => {
    if (!projectId || !userInfo?.id) {
      return
    }
    fetchOrganizations()
  }, [projectId, userInfo?.id])

  const fetchOrganizations = async () => {
    try {
      setLoading(true)
      handleResetData()
      const response = await getProjectSyncOrganizationsMiddleware(projectId)
      if (response.data.organizations.length) {
        const newOrganizations = response.data.organizations.map((el) => ({
          label: el.name,
          value: el.id,
          db_name: el.db_name,
        }))
        if (!userInfo.last_organization_id) {
          setSourceOrganizations([
            {
              label: "Personal",
              value: "",
              db_name: "",
            },
            ...newOrganizations,
          ])
          setSelectedSourceOrganization({
            label: "Personal",
            value: "",
            db_name: "",
          })
        } else {
          setSourceOrganizations(newOrganizations)
          const lastOrganization = newOrganizations.find(
            (el) => el.value === userInfo.last_organization_id
          )
          if (lastOrganization) {
            setSelectedSourceOrganization(lastOrganization)
          }
        }
        setDestinationOrganizations(newOrganizations)
      } else {
        setError(response.message || "")
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to load workspaces")
    } finally {
      setLoading(false)
    }
  }

  const handleChangeSourceOrganization = (changedData: {
    label: string
    value: string
    db_name: string
  }) => {
    if (changedData.value === selectedSourceOrganization.value) {
      return
    }
    setSelectedSourceOrganization(changedData)
    setSelectedDestinationOrganization({
      label: "",
      value: "",
      db_name: "",
    })
    setErrorDestination("")
    setProjectSyncOrganization({
      source: {
        project: {
          code: "",
          id: "",
          fullCode: "",
        },
      },
      destination: {
        hasProject: false,
        project: {
          code: "",
          id: "",
          fullCode: "",
        },
        components: [],
      },
    })
    setSelectSyncComponent({})
  }

  const handleChangeDestinationOrganization = async (changedData: {
    label: string
    value: string
    db_name: string
  }) => {
    if (changedData.value === selectedDestinationOrganization.value) {
      return
    }
    setSelectSyncComponent({})
    setErrorDestination("")
    setProjectSyncOrganization({
      source: {
        project: {
          code: "",
          id: "",
          fullCode: "",
        },
      },
      destination: {
        hasProject: false,
        project: {
          code: "",
          id: "",
          fullCode: "",
        },
        components: [],
      },
    })
    setPaginationState({
      page: 1,
      pageSize: 25,
    })
    totalPage.setValue(0)
    const sourceId = selectedSourceOrganization.value
    const sourceDbName = selectedSourceOrganization.db_name
    const destinationId = changedData.value
    const destinationDbName = changedData.db_name
    if (sourceId === destinationId) {
      toast(
        <LabelNotificationPage
          messenger="Source and destination workspaces cannot be the same!"
          type="error"
        />
      )
      return
    }
    setSelectedDestinationOrganization(changedData)
    try {
      const response = await getOrganizationProjectDataMiddleware(
        projectId,
        sourceId,
        sourceDbName,
        destinationId,
        destinationDbName
      )
      setProjectSyncOrganization(response.data)
      // Set total page for pagination
      totalPage.setValue(response.data.destination.components.length)
    } catch (error: any) {
      setErrorDestination(
        error?.response?.data?.message || "Failed to load workspace data"
      )
    }
  }

  const handleSelectSyncComponent = (component: any) => async () => {
    if (selectSyncComponent[component.id]) {
      const newSelectSyncComponent = cloneDeep(selectSyncComponent)
      delete newSelectSyncComponent[component.id]
      setSelectSyncComponent(newSelectSyncComponent)
      return
    }
    const projectKeys = JSON.parse(
      localStorage.getItem(EncryptionKeys.projectEncryptionKeys) || "{}"
    )
    const projectKey = projectKeys[projectId]
    let commit = `Sync commit from ${selectedSourceOrganization.db_name.toUpperCase()}/${
      projectSyncOrganization.source.project.code
    }/${component.code}`
    if (projectKey) {
      const decryptedCommit = await encryptionGCMHelper.decryptContext(
        projectKey,
        component.historyCommit
      )
      commit = await encryptionGCMHelper.encryptContext(
        projectKey,
        `${decryptedCommit} - ${commit}`
      )
    }
    const componentCodeSplit = component.code.split("-")
    const newComponentCode = `${projectSyncOrganization.destination.project.fullCode}-${componentCodeSplit[1]}`
    setSelectSyncComponent({
      ...selectSyncComponent,
      [component.id]: {
        historyId: component.historyId,
        componentId: component.id,
        commit,
        sourceCode: `${selectedSourceOrganization.db_name.toUpperCase()}/${
          projectSyncOrganization.source.project.code
        }/${component.code}/${component.historyCode}`,
        destinationCode: `${projectSyncOrganization.destination.project.code}/${projectSyncOrganization.destination.project.code}-${componentCodeSplit[1]}`,
        newComponentCode,
        oldComponentCode: `${projectSyncOrganization.source.project.fullCode}-${componentCodeSplit[1]}`,
        oldProjectCode: projectSyncOrganization.source.project.fullCode,
        newProjectCode: projectSyncOrganization.destination.project.fullCode,
      },
    })
  }

  const handleSelectAllComponents = async () => {
    // Filter out components that are disabled (have destination with historyCode)
    const selectableComponents =
      projectSyncOrganization.destination.components.filter(
        (component) =>
          !(component.destination && component.destination.historyCode)
      )

    const allSelected = selectableComponents.every(
      (component) => selectSyncComponent[component.source?.id || ""]
    )

    if (allSelected) {
      toast(
        <LabelNotificationPage
          messenger={`All components are already the latest version!`}
          type="warning"
        />
      )
      // If all selectable components are selected, deselect all
      setSelectSyncComponent({})
    } else {
      // If not all are selected, select all selectable components
      const newSelectSyncComponent: Record<string, any> = {}
      const projectKeys = JSON.parse(
        localStorage.getItem(EncryptionKeys.projectEncryptionKeys) || "{}"
      )
      const projectKey = projectKeys[projectId]

      for (const component of selectableComponents) {
        let commit = `Sync commit from ${selectedSourceOrganization.db_name.toUpperCase()}/${
          projectSyncOrganization.source.project.code
        }/${component.source.code}`

        if (projectKey) {
          const decryptedCommit = await encryptionGCMHelper.decryptContext(
            projectKey,
            component.source.historyCommit
          )
          commit = await encryptionGCMHelper.encryptContext(
            projectKey,
            `${decryptedCommit} - ${commit}`
          )
        }

        const componentCodeSplit = component.source.code.split("-")
        const newComponentCode = `${projectSyncOrganization.destination.project.fullCode}-${componentCodeSplit[1]}`
        newSelectSyncComponent[component.source.id] = {
          historyId: component.source.historyId,
          componentId: component.source.id,
          commit,
          sourceCode: `${selectedSourceOrganization.db_name.toUpperCase()}/${
            projectSyncOrganization.source.project.code
          }/${component.source.code}/${component.source.historyCode}`,
          destinationCode: `${projectSyncOrganization.destination.project.code}/${projectSyncOrganization.destination.project.code}-${componentCodeSplit[1]}`,
          newComponentCode,
          oldComponentCode: `${projectSyncOrganization.source.project.fullCode}-${componentCodeSplit[1]}`,
          oldProjectCode: projectSyncOrganization.source.project.fullCode,
          newProjectCode: projectSyncOrganization.destination.project.fullCode,
        }
      }

      setSelectSyncComponent(newSelectSyncComponent)
    }
  }

  const handleResetData = () => {
    setSelectSyncComponent({})
    setProjectSyncOrganization({
      source: {
        project: {
          code: "",
          id: "",
          fullCode: "",
        },
      },
      destination: {
        hasProject: false,
        project: {
          code: "",
          id: "",
          fullCode: "",
        },
        components: [],
      },
    })
    setError(null)
    setErrorDestination("")
    setSelectedDestinationOrganization({
      label: "",
      value: "",
      db_name: "",
    })
    setSelectedSourceOrganization({
      label: "",
      value: "",
      db_name: "",
    })
    setSourceOrganizations([])
    setDestinationOrganizations([])
    // Reset pagination
    setPaginationState({
      page: 1,
      pageSize: 25,
    })
    totalPage.setValue(0)
  }

  const handleChangePage = (newPage: number, newLimit: number) => {
    setPaginationState({
      page: newPage,
      pageSize: newLimit,
    })
  }

  const onSyncComponents = async () => {
    const log = `sync data components project ${selectedSourceOrganization.db_name.toUpperCase()}/${
      projectSyncOrganization.source.project.code
    } to 
    workspace ${selectedDestinationOrganization.label}/${
      projectSyncOrganization.destination.project.code
    }`
    const sourceId = selectedSourceOrganization.value
    const sourceDbName = selectedSourceOrganization.db_name
    const destinationId = selectedDestinationOrganization.value
    const destinationDbName = selectedDestinationOrganization.db_name
    configureStore.dispatch(
      openModal({
        type: "Delete",
        props: {
          deleteModal: {
            title: `Are you sure you want to ${log}?`,
            label: `Warning`,
            content: `This action cannot be undone.`,
            onSubmit: async () => {
              try {
                configureStore.dispatch(startLoading())
                const componentHistoryLogs = Object.values(selectSyncComponent)
                const componentHistoryLogsJson =
                  JSON.stringify(componentHistoryLogs)
                await syncCommitToOrganizationMiddleware(projectId, {
                  component_history_logs: componentHistoryLogsJson,
                  sourceId,
                  sourceDbName,
                  destinationId,
                  destinationDbName,
                  log,
                  projectCode:
                    projectSyncOrganization.destination.project.fullCode,
                })
                if (!projectSyncOrganization.destination.hasProject) {
                  await handleUserShareKeyProjectMiddleware(
                    true,
                    false,
                    JSON.stringify([projectId]),
                    undefined,
                    undefined,
                    destinationDbName
                  )
                  await addRoleGroupOrganizationMiddleware({
                    is_share_all_project: true,
                    project_ids: JSON.stringify([projectId]),
                    dbName: destinationDbName,
                  })
                }

                handleResetData()
                toast(
                  <LabelNotificationPage
                    messenger="Sync project to workspace successfully!"
                    type="success"
                  />
                )
                getUserMiddleware(() => {
                  closeLoading()
                  pushTo(PATH.projects)
                })
                closeModal()
              } catch (error: any) {
                closeModal()
                closeLoading()
                toast(
                  <LabelNotificationPage
                    messenger={
                      error?.response?.data?.message ||
                      "Sync project to workspace failed!"
                    }
                    type="error"
                  />
                )
              }
            },
            styleTitle: { textAlign: "center" },
            titleButton: "Confirm",
            colorYellowButton: true,
          },
        },
      })
    )
  }

  const renderComponent = () => {
    const { page, pageSize } = paginationState
    const startIndex = (page - 1) * pageSize
    const endIndex = startIndex + pageSize
    const paginatedComponents =
      projectSyncOrganization.destination.components.slice(startIndex, endIndex)

    return (
      <div className="flex flex-col mt-[8px]">
        {paginatedComponents.map((component, index) => {
          return (
            <div
              key={index}
              className="flex flex-col p-[8px] border border-[#E4E4E4] rounded-[8px] mb-[8px] bg-[#FFFFFF]"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 flex items-center">
                  {component.destination &&
                  component.destination.historyCode ? (
                    <div className="w-[20px] min-w-[20px] mr-[8px]"></div>
                  ) : (
                    <CheckedDefault
                      className="mr-[8px]"
                      checked={Boolean(
                        selectSyncComponent[component.source?.id || ""]
                      )}
                      onClick={handleSelectSyncComponent(component.source)}
                    />
                  )}

                  <TypeComponent label={component.source.type} />
                  <p className="text-13 text-[#111111]  ml-[8px]">
                    {`${component.source.code}/`}
                    <span className="font-bold">
                      {component.source.historyCode}
                    </span>
                  </p>
                </div>
                <div
                  className="flex items-center justify-center"
                  style={{ width: "50px" }}
                >
                  <IconArrowRight />
                </div>
                <div className="flex-1 flex flex-col">
                  {component.isNewComponent ? (
                    <p className=" text-13 text-[#111111]">
                      <span className="font-bold">
                        {`New component to ${projectSyncOrganization.destination.project.code}`}
                      </span>
                    </p>
                  ) : component.destination &&
                    component.destination.historyCode ? (
                    <p className="text-13 text-[#111111]">
                      {`${component.destination?.code}/`}
                      <span className="font-bold">
                        {component.destination?.historyCode}
                      </span>
                    </p>
                  ) : (
                    <p className="text-13 text-[#111111]">
                      {`${component.destination?.code}`}
                    </p>
                  )}
                </div>
              </div>
              {component.destination && component.destination.historyCode ? (
                <p className="text-13 text-[#E87A16] mt-[6px] ml-[20px]">
                  This version is already the latest
                </p>
              ) : null}
              <p></p>
            </div>
          )
        })}
      </div>
    )
  }

  const renderError = (newError: string) => {
    return (
      <div
        className="flex flex-col justify-center p-2 mt-[8px] mb-[24px]"
        style={{
          background: "rgba(232, 122, 22, 0.20)",
          borderRadius: 6,
        }}
      >
        <div className="flex items-center">
          <IconCheckWarning
            style={{
              minWidth: 24,
            }}
          />
          <p
            className="text-13 pl-1"
            style={{
              color: "#E87A16",
            }}
          >
            {newError || ""}
          </p>
        </div>
      </div>
    )
  }

  const renderSelectComponent = () => {
    if (errorDestination) {
      return <div className="mt-[12px]">{renderError(errorDestination)}</div>
    }
    if (projectSyncOrganization?.destination.components.length) {
      return (
        <div className="mt-[12px] flex flex-col">
          {Object.keys(selectSyncComponent).length > 0 ? (
            <div
              className="flex items-center gap-2 p-2 mb-[4px]"
              style={{
                background: "rgba(10, 90, 245, 0.15)",
                borderRadius: 6,
              }}
            >
              <Tippy
                className="custom-tippy-menu-left flex-1"
                placement="right"
                content={
                  <span
                    className="font-normal"
                    style={{
                      fontSize: 13,
                      lineHeight: "22px",
                    }}
                  >
                    Remove all items
                  </span>
                }
              >
                <div>
                  <IconRemove
                    onClick={() => setSelectSyncComponent({})}
                    className="remove-sync"
                    style={{
                      cursor: "pointer",
                      width: 24,
                      height: 24,
                    }}
                  />
                </div>
              </Tippy>

              <p className="text-[14px] text-[#0A5AF5]">
                {`${Object.keys(selectSyncComponent).length} items selected`}
              </p>
            </div>
          ) : null}
          <div className="flex flex-col p-[8px]">
            <div className="flex items-center justify-between">
              <div className="flex-1 flex items-center">
                <Tippy
                  className="custom-tippy-menu-left flex-1"
                  placement="right"
                  content={
                    <span
                      className="font-normal"
                      style={{
                        fontSize: 13,
                        lineHeight: "22px",
                      }}
                    >
                      Select all components
                    </span>
                  }
                >
                  <div className="mr-[8px]">
                    <CheckedDefault
                      checked={(() => {
                        const selectableComponents =
                          projectSyncOrganization.destination.components.filter(
                            (component) =>
                              !(
                                component.destination &&
                                component.destination.historyCode
                              )
                          )
                        return (
                          selectableComponents.length > 0 &&
                          selectableComponents.every(
                            (component) =>
                              selectSyncComponent[component.source?.id || ""]
                          )
                        )
                      })()}
                      onClick={handleSelectAllComponents}
                    />
                  </div>
                </Tippy>
                <p className="text-13 text-[#111111] font-bold">{`${selectedSourceOrganization.label}/${projectSyncOrganization.source.project.code}`}</p>
              </div>
              <div
                className="flex items-center justify-center"
                style={{ width: "50px" }}
              ></div>
              <div className="flex-1 flex flex-col">
                {projectSyncOrganization?.destination?.project?.code ? (
                  <p className="text-13 text-[#111111] font-bold">{`${selectedDestinationOrganization.label}/${projectSyncOrganization.destination.project.code}`}</p>
                ) : null}
              </div>
            </div>
          </div>

          {renderComponent()}

          {/* Pagination */}
          {totalPage.value > 0 && (
            <div className="mt-[8px] mb-[24px]">
              <Pagination
                total={totalPage.value}
                queryState={{
                  page: paginationState.page,
                  limit: paginationState.pageSize,
                }}
                handleChangePage={handleChangePage}
              />
            </div>
          )}
        </div>
      )
    }
    return null
  }

  const renderBody = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-full">
          <div className="text-center">
            <HashLoader color="#F7AC1B" loading={true} size={50} />
            <p className="mt-[48px] text-[#111111]">Loading workspaces...</p>
          </div>
        </div>
      )
    }
    if (error) {
      return renderError(error)
    }
    return (
      <div className="flex flex-col">
        <div className="flex items-center">
          <div className="flex-1 flex flex-col px-[8px] py-[12px] border border-[#E4E4E4] rounded-[8px] bg-[#FFFFFF]">
            <p className="text-13 font-semibold text-[#111111] mb-[8px]">
              Source Repository
            </p>
            <SelectMulti
              options={sourceOrganizations}
              selectedOption={selectedSourceOrganization}
              handleChange={handleChangeSourceOrganization}
              isMulti={false}
              isDisabled
              noOptionsMessage
            />
          </div>
          <div
            className="flex items-center justify-center"
            style={{ width: "50px" }}
          >
            <IconArrowRight />
          </div>
          <div className="flex-1 flex flex-col px-[8px] py-[12px] border border-[#E4E4E4] rounded-[8px] bg-[#FFFFFF]">
            <p className="text-13 font-semibold text-[#111111] mb-[8px]">
              Destination Repository
            </p>
            <SelectMulti
              options={destinationOrganizations}
              selectedOption={selectedDestinationOrganization}
              handleChange={handleChangeDestinationOrganization}
              isMulti={false}
              noOptionsMessage
              isDisabled={!selectedSourceOrganization.label}
            />
          </div>
        </div>
        {renderSelectComponent()}
      </div>
    )
  }

  return (
    <PageLayout
      childrenHeader={
        <div className="flex items-center justify-between h-full px-[24px]">
          <h2 className="text-[16px] font-semibold lh-26 text-black">
            Sync to Workspace
          </h2>
          {!loading ? (
            <Button
              widthBtn={200}
              title="Sync"
              onClick={onSyncComponents}
              disabled={Object.keys(selectSyncComponent).length === 0}
              isDisabledBtn
            />
          ) : null}
        </div>
      }
    >
      <div className="flex flex-col p-6 h-full">{renderBody()}</div>
    </PageLayout>
  )
}
